import React from 'react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/utils'
import Link from 'next/link'
import { Arrow } from '@/lib/icons'

interface JourneyStepProps {
    date: string
    title: string
    description: string
    icon: React.ReactNode
    isLeft?: boolean
    videoId?: string
    technologies?: string[]
    linkTo?: { url: string; text: string }
    index: number
}

const TechLogos = ({ technologies }: { technologies: string[] }) => {
    return (
        <div className="flex flex-wrap gap-2">
            {technologies.map((tech, index) => (
                <div
                    key={index}
                    className="flex items-center gap-1 px-2 py-1 bg-light/5 rounded-md border border-light/10 hover:border-accent/30 transition-colors duration-200 group"
                    title={tech}
                >
                    <span className="text-xs text-light/70 font-medium group-hover:text-accent transition-colors duration-200">
                        {tech}
                    </span>
                </div>
            ))}
        </div>
    )
}


const JourneyStep: React.FC<JourneyStepProps> = ({
    date,
    title,
    description,
    icon,
    isLeft = false,
    videoId,
    technologies = [],
    linkTo,
    index
}) => {
    const [isVisible, setIsVisible] = useState(false)
    const [showVideo, setShowVideo] = useState(false)
    const stepRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setTimeout(() => setIsVisible(true), index * 100)
                }
            },
            { threshold: 0.1 }
        )

        if (stepRef.current) {
            observer.observe(stepRef.current)
        }

        return () => observer.disconnect()
    }, [index])

    return (
        <div
            ref={stepRef}
            className={cn(
                "relative flex items-center w-full bsp4-6 md:mb-16",
                isLeft ? "md:flex-row-reverse" : "md:flex-row"
            )}
        >
            <div className="md:hidden absolute left-4 transform -translate-x-1/2 z-10">
                <div
                    className={cn(
                        "w-4 h-4 bg-accent ml-1 rounded-full border-2 border-dark shadow-lg transition-all duration-700 transform",
                        isVisible ? "scale-100" : "scale-0"
                    )}
                />
                <div
                    className={cn(
                        "absolute top-1/2 -translate-y-1/2 left-3 w-10 h-1 bg-gradient-to-r from-accent/50 to-transparent transition-all duration-700",
                        isVisible ? "opacity-100" : "opacity-0"
                    )}
                />
            </div>

            <div
                className={cn(
                    "mt-4 w-full md:w-5/12 ml-14 mr-2 md:mx-0 transition-all duration-700 transform",
                    isVisible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-8 opacity-0",
                    isLeft ? "md:pr-8" : "md:pl-8"
                )}
            >
                <div className="bg-gradient-to-br from-dark2 via-dark3 to-dark2 border-2 border-light/10 rounded-xl p-4 md:p-4 lg:p-6 shadow-xl transition-all duration-300">
                    <div className="flex items-start gap-2 mb-3">
                        <div className="w-10 mt-0.5 h-10 flex items-center justify-center relative rounded-md bg-gradient-to-br from-accent/30 to-accent/10 text-accent transition-all duration-300">
                            {icon}
                        </div>
                        <div className="flex-1">
                            <h3 className="font-bold text-lg md:text-xl text-light transition-colors duration-300">
                                {title}
                            </h3>
                            <p className="text-accent text-sm font-semibold -mt-1 opacity-80">
                                {date}
                            </p>
                        </div>
                    </div>

                    <p className="text-light/80 text-sm md:text-base leading-relaxed mb-3 transition-colors duration-300">
                        {description}
                    </p>

                    {technologies.length > 0 && (
                        <TechLogos technologies={technologies} />
                    )}

                    {videoId && (
                        <div className="mt-4">
                            <button
                                onClick={() => setShowVideo(!showVideo)}
                                className="inline-flex items-center gap-2 text-accent text-sm font-medium transition-all duration-200 group/btn"
                            >
                                <div className="p-1 rounded-full bg-accent/20 group-hover/btn:bg-accent/30 transition-colors duration-200">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M8 5v14l11-7z" />
                                    </svg>
                                </div>
                                {showVideo ? 'Hide Demo' : 'Watch Demo'}
                            </button>

                            {showVideo && (
                                <div className="mt-1 rounded-xl overflow-hidden border border-light/10 shadow-lg animate-in slide-in-from-top duration-300">
                                    <div className="relative aspect-video">
                                        <iframe
                                            width="100%"
                                            height="100%"
                                            src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
                                            title={title}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            className="absolute inset-0 w-full h-full"
                                        ></iframe>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {linkTo && (
                        <div className="mt-4">
                            <Link
                                href={linkTo.url}
                                className="inline-flex items-center gap-1 px-4 py-2 bg-accent text-dark rounded-lg font-semibold text-sm hover:bg-accent/80 transition-all duration-200 hover:shadow-lg group"
                            >
                                <span>{linkTo.text}</span>
                                <Arrow className='text-2xl pt-0.5 group-hover:translate-x-1 transition-all transform duration-300' />
                            </Link>
                        </div>
                    )}
                </div>
            </div>

            <div className="hidden md:flex items-center justify-center w-2/12 relative">
                <div
                    className={cn(
                        "absolute w-[34%] h-0.5 bg-gradient-to-r transition-all duration-700 delay-300",
                        isLeft
                            ? "right-5 from-accent/50 to-transparent"
                            : "left-5 from-transparent to-accent/50",
                        isVisible ? "opacity-100" : "opacity-0"
                    )}
                />

                <div
                    className={cn(
                        "w-5 h-5 bg-accent rounded-full border-4 border-dark shadow-lg transition-all duration-700 transform",
                        isVisible ? "scale-100" : "scale-0"
                    )}
                >
                    <div className="w-full h-full bg-accent/50 rounded-full" />
                </div>
            </div>
        </div>
    )
}

export default JourneyStep